// ProjectsPage — All projects (pen "Projects and the Studio: in and out",
// T1, decided 29 Oct), pure.
//
// The title and its lede, then the toolbar: search on the left; Sort and
// the view switch (Cards | Table) on the right. Both views share the sort
// and the view, remembered per device (projectsView.ts). Cards: pinned
// projects first (PINNED, in pin order), then the others, each by the sort;
// the open project leads its section. Table: one sorted table whose Name and
// Changed headings sort it (a second click reverses), the heading fixed
// while it scrolls. There is no assistant here: it lives in the Studio.
//
// Props-driven: the items (with their actions), their summaries and the view
// come in; every choice reports up (`ProjectsViewCommand`, `RecentListCommand`
// for pins). The search text is view-ephemeral (it does not survive a
// reload), so it is local state.

export { ProjectsPage };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import type {
    RecentListCommand,
    RecentListView,
} from "../Sidebar/recentList.js";
import { useRowMenu } from "../Sidebar/SidebarRecent.js";
import type { RecentItem } from "../Sidebar/sidebarEntries.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { TextTabs } from "../TextTabs/TextTabs.js";
import { ViewColumn, ViewHeader } from "../ViewHeader/ViewHeader.js";
import { ProjectCards, ProjectTable } from "./ProjectsViews.js";
import type { ProjectSummary } from "./projects.js";
import {
    matchesQuery,
    PROJECTS_SORTS,
    type ProjectsLayout,
    type ProjectsOrder,
    type ProjectsSort,
    type ProjectsView,
    type ProjectsViewCommand,
    sortProjects,
} from "./projectsView.js";
import "./Projects.css";

type ProjectsPageProps = {
    readonly items: readonly RecentItem[];
    // The archived projects (the Archived tab: Restore, Delete …).
    readonly archived: readonly RecentItem[];
    // What each project holds and where the author was in it.
    readonly summaryOf: (id: string) => ProjectSummary | undefined;
    // Pins (shared with the left menu).
    readonly pins: RecentListView;
    readonly onPinsCommand: (cmd: RecentListCommand) => void;
    readonly view: ProjectsView;
    readonly onView: (cmd: ProjectsViewCommand) => void;
    // The projects are still being read: skeletons in the real shape.
    readonly loading: boolean;
    // Set up a new project (the empty state's way forward); null while one
    // is being set up.
    readonly onNewProject: (() => void) | null;
};

// The search field takes at most this many characters (a ceiling).
const QUERY_MAX = 200;
const SKELETONS = 3;
const ORDERS: readonly ProjectsOrder[] = ["desc", "asc"];

type ProjectsTab = "active" | "archived";

const layoutOf = (value: string): ProjectsLayout | undefined =>
    value === "cards" || value === "table" ? value : undefined;

const useSortWords = () => {
    const { t } = useLingui();
    return {
        sort: (sort: ProjectsSort): string => {
            switch (sort) {
                case "changed":
                    return t`Last changed`;
                case "title":
                    return t`Title`;
                default:
                    return exhaustSwitchCase(sort);
            }
        },
        order: (sort: ProjectsSort, order: ProjectsOrder): string => {
            switch (sort) {
                case "changed":
                    return order === "desc" ? t`Newest first` : t`Oldest first`;
                case "title":
                    return order === "asc" ? t`A to Z` : t`Z to A`;
                default:
                    return exhaustSwitchCase(sort);
            }
        },
    };
};

// The open project first, the rest in the given order.
const openFirst = (items: readonly RecentItem[]): readonly RecentItem[] => [
    ...items.filter((i) => i.current),
    ...items.filter((i) => !i.current),
];

const ProjectsPage = ({
    items,
    archived,
    summaryOf,
    pins,
    onPinsCommand,
    view,
    onView,
    loading,
    onNewProject,
}: ProjectsPageProps): ReactNode => {
    const { t, i18n } = useLingui();
    const words = useSortWords();
    const menu = useRowMenu(onPinsCommand, "page");
    // View state: the tab and the search text (a reload starts afresh).
    const [tab, setTab] = useState<ProjectsTab>("active");
    const [query, setQuery] = useState("");
    const showing: ProjectsTab = archived.length === 0 ? "active" : tab;
    const pool = showing === "active" ? items : archived;
    const withChange = (item: RecentItem) => {
        const changedAt = summaryOf(item.id)?.changedAt;
        return changedAt === undefined ? item : { ...item, changedAt };
    };
    const found = sortProjects(
        pool
            .filter((i) => matchesQuery(i.title, query, i18n.locale))
            .map(withChange),
        view,
        i18n.locale,
    );

    const toolbar = (
        <div className="bilda-projects__toolbar">
            <label className="bilda-projects__search">
                <Icon name="search" />
                <input
                    type="search"
                    value={query}
                    maxLength={QUERY_MAX}
                    placeholder={t`Search projects`}
                    aria-label={t`Search projects`}
                    onChange={(e) => setQuery(e.currentTarget.value)}
                />
            </label>
            <Menu
                align="start"
                contentClassName="bilda-projects-sort-menu"
                trigger={
                    <button
                        type="button"
                        className="bilda-projects__sort"
                        aria-label={t`Sort: ${words.sort(view.sort)}, ${words.order(view.sort, view.order)}`}
                    >
                        <Icon name="sort" />
                        {words.sort(view.sort)}
                        <Icon name="chevron-down" />
                    </button>
                }
                items={[
                    { _tag: "label", label: t`Sort by` },
                    ...PROJECTS_SORTS.map(
                        (sort): MenuItem => ({
                            _tag: "item",
                            label: words.sort(sort),
                            checked: view.sort === sort,
                            onSelect: () => onView({ type: "sort-by", sort }),
                        }),
                    ),
                    { _tag: "separator" },
                    ...ORDERS.map(
                        (order): MenuItem => ({
                            _tag: "item",
                            label: words.order(view.sort, order),
                            checked: view.order === order,
                            onSelect: () => onView({ type: "order-by", order }),
                        }),
                    ),
                ]}
            />
            <SegmentedControl
                size="sm"
                ariaLabel={t`View`}
                value={view.layout}
                onChange={(value) => {
                    const layout = layoutOf(value);
                    if (layout !== undefined)
                        onView({ type: "show-as", layout });
                }}
                options={[
                    {
                        value: "cards",
                        label: t`Cards`,
                        icon: <Icon name="layout-grid" />,
                    },
                    {
                        value: "table",
                        label: t`Table`,
                        icon: <Icon name="table" />,
                    },
                ]}
            />
        </div>
    );

    const cards = (
        label: string,
        rows: readonly RecentItem[],
        pinned: boolean,
    ) =>
        rows.length === 0 ? null : (
            <section className="bilda-projects__section" aria-label={label}>
                {pins.pinned.length > 0 && showing === "active" ? (
                    <h2 className="bilda-projects__label">{label}</h2>
                ) : null}
                <ProjectCards
                    items={rows}
                    summaryOf={summaryOf}
                    pinned={pinned}
                    menu={menu}
                />
            </section>
        );

    const body = (): ReactNode => {
        if (loading)
            return (
                <ul className="bilda-projects__grid" aria-busy="true">
                    {Array.from({ length: SKELETONS }, (_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholders
                        <li key={i} className="bilda-projects__skeleton">
                            <Skeleton width="100%" height="15rem" />
                        </li>
                    ))}
                </ul>
            );
        if (items.length === 0 && showing === "active")
            return (
                <EmptyState
                    title={t`No projects yet`}
                    body={t`A project is a free surface for sources and ideas.`}
                >
                    <Button
                        variant="primary"
                        disabled={onNewProject === null}
                        onClick={onNewProject ?? undefined}
                    >
                        {t`New project`}
                    </Button>
                </EmptyState>
            );
        if (found.length === 0)
            return (
                <p className="bilda-projects__none" role="status">
                    {t`No projects match "${query}"`}
                </p>
            );
        if (view.layout === "table")
            return (
                <ProjectTable
                    items={found}
                    summaryOf={summaryOf}
                    pinnedIds={pins.pinned}
                    menu={menu}
                    view={view}
                    onView={onView}
                />
            );
        if (showing === "archived") return cards(t`Archived`, found, false);
        const pinned = pins.pinned.flatMap((id) =>
            found.filter((i) => i.id === id),
        );
        const others = found.filter((i) => !pins.pinned.includes(i.id));
        return (
            <>
                {cards(t`Pinned`, openFirst(pinned), true)}
                {cards(t`Others`, openFirst(others), false)}
            </>
        );
    };

    // Active n · Archived n (the Archived tab once something is archived).
    const tabs =
        archived.length === 0 ? null : (
            <TextTabs<ProjectsTab>
                ariaLabel={t`Show projects`}
                value={showing}
                onChange={setTab}
                tabs={[
                    { value: "active", label: t`Active ${items.length}` },
                    {
                        value: "archived",
                        label: t`Archived ${archived.length}`,
                    },
                ]}
            />
        );

    return (
        <div className="bilda-projects">
            <ViewColumn className="bilda-projects__column">
                <ViewHeader
                    title={t`Projects`}
                    tools={
                        <p className="bilda-projects__lede">
                            {t`Everything you are making. Pick one up where you left off.`}
                        </p>
                    }
                    nav={
                        items.length === 0 && archived.length === 0 ? null : (
                            <div className="bilda-projects__nav">
                                {tabs}
                                {toolbar}
                            </div>
                        )
                    }
                />
                {body()}
            </ViewColumn>
        </div>
    );
};
